import { SORT_KEY, SORT_ORDER } from '../model/slice/appSlice'

/**
 * Interface for items that can be sorted
 */
interface SortableItem {
  name?: string
  type?: string
  ext?: string
  created?: string
  updated?: string
  [key: string]: any
}

/**
 * Interface for sort parameters
 */
interface SortParams {
  sortDirection: string
  sortKey: SORT_KEY
}

/**
 * Compare two strings case-insensitively
 */
const compareStrings = (a: string, b: string): number => {
  const A = a.toLowerCase()
  const B = b.toLowerCase()
  if (A === B) return 0
  return A < B ? -1 : 1
}

/**
 * Compare two strings case-sensitively
 */
const compareNames = (a: string, b: string): number => {
  if (a === b) return 0
  return a < b ? -1 : 1
}

/**
 * Apply sort direction to comparison result
 */
const applyDirection = (comparison: number, sortDirection: string): number => {
  return sortDirection === 'asc' ? comparison : -comparison
}

/**
 * Compare two dates and return the comparison result
 */
const compareDates = (dateA: string | undefined, dateB: string | undefined): number | null => {
  const aDate = new Date(dateA || '')
  const bDate = new Date(dateB || '')

  if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
    return aDate.getTime() - bDate.getTime()
  }

  return null
}

/**
 * Sort comparer function for items
 */
export const sortComparer = (
  { sortDirection, sortKey }: SortParams,
  a: SortableItem,
  b: SortableItem
): number => {
  const aName = a.name || ''
  const bName = b.name || ''
  // For EXT, FILE_TYPE, and NAME sorting, we need secondary sorting by name
  if (sortKey === SORT_KEY.EXT) {
    const aExt = a.ext || ''
    const bExt = b.ext || ''

    // First compare by extension
    const extComparison = compareStrings(aExt, bExt)
    if (extComparison !== 0) {
      return applyDirection(extComparison, sortDirection)
    }

    // If extensions are the same, sort by name
    const nameComparison = compareNames(aName, bName)
    return applyDirection(nameComparison, sortDirection)
  }

  if (sortKey === SORT_KEY.FILE_TYPE || sortKey === SORT_KEY.NAME) {
    const aType = a.type || ''
    const bType = b.type || ''

    // For NAME sorting, we first compare by file type
    // For FILE_TYPE sorting, we first compare by type
    const typeComparison = compareStrings(aType, bType)
    if (typeComparison !== 0) {
      return applyDirection(typeComparison, sortDirection)
    }

    // If types are the same, sort by name
    const nameComparison = compareNames(aName, bName)
    return applyDirection(nameComparison, sortDirection)
  }

  if (sortKey === SORT_KEY.UPLOAD_DATE) {
    const comparison = compareDates(a.created, b.created)
    if (comparison !== null && comparison !== 0) {
      return applyDirection(comparison, sortDirection)
    }

    // if dates are the same, sort by name
    const nameComparison = compareNames(aName, bName)
    return applyDirection(nameComparison, sortDirection)
  }

  if (sortKey === SORT_KEY.LAST_UPDATED) {
    const comparison = compareDates(a.updated, b.updated)
    if (comparison !== null && comparison !== 0) {
      return applyDirection(comparison, sortDirection)
    }

    // if dates are the same, sort by name
    const nameComparison = compareNames(aName, bName)
    return applyDirection(nameComparison, sortDirection)
  }

  // Default case: compare by name as fallback
  return applyDirection(compareNames(aName, bName), sortDirection)
}

/**
 * Get the order by string for API requests
 */
export const getOrderBy = (sortType: SORT_KEY, sortOrder: SORT_ORDER): string => {
  // Default sorting is by Name but we use FILE_TYPE for the API
  const order = sortType === SORT_KEY.NAME ? SORT_KEY.FILE_TYPE : sortType
  return sortOrder === SORT_ORDER.ASC ? order : `-${order}`
}
